import { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../components/ui/Toast'
import { CardGridSkeleton } from '../components/ui/Skeletons'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface PortGroup {
  type: string; count: number; startX: number; startY: number; spacing: number; label: string; poe?: boolean
}
interface LEDConfig { type: string; x: number; y: number; size: number; color?: string }
interface ExtraElement { type: string; x: number; y: number; width?: number; height?: number }
interface DesignData { portGroups: PortGroup[]; leds: LEDConfig[]; extras?: ExtraElement[]; labels?: { text: string; x: number; y: number; fontSize?: number }[] }

interface ModuleData {
  id: string; name: string; manufacturer: string | null; model_number: string | null; mpn: string | null
  form_factor: string; chassis_color: string; chassis_material: string; chassis_width: number; chassis_height: number
  corner_radius: number; mounting_ears: number; design_data: string | null; svg_cache: string | null
  status: string; is_template: number; tags: string | null; catalog_product_id: string | null; device_spec_id: string | null
}

interface PortType { id: string; name: string; label: string; width: number; height: number; supports_poe: number }

const MATERIALS = [
  { value: 'matte_black', label: 'Matte Black' },
  { value: 'glossy_black', label: 'Glossy Black' },
  { value: 'brushed_aluminum', label: 'Brushed Aluminum' },
  { value: 'silver', label: 'Silver' },
]

const LED_TYPES = ['system', 'poe', 'link', 'battery', 'error']

export default function DeviceModuleDesigner() {
  const { id } = useParams<{ id: string }>()
  const [module, setModule] = useState<ModuleData | null>(null)
  const [design, setDesign] = useState<DesignData>({ portGroups: [], leds: [] })
  const [portTypes, setPortTypes] = useState<PortType[]>([])
  const [svgPreview, setSvgPreview] = useState('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<'chassis' | 'ports' | 'leds' | 'labels' | 'linking'>('chassis')
  const [portPreviewMode, setPortPreviewMode] = useState<'normal' | 'all_off' | 'all_active' | 'mixed' | 'poe'>('normal')
  const [catalogSearch, setCatalogSearch] = useState('')
  const [catalogResults, setCatalogResults] = useState<{ id: string; name: string; mpn: string }[]>([])
  const [deviceSpecResults, setDeviceSpecResults] = useState<{ id: string; name: string }[]>([])
  const [dragging, setDragging] = useState<{ type: 'port' | 'led'; index: number; startX: number; startY: number; origX: number; origY: number } | null>(null)
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout>>(undefined)
  const navigate = useNavigate()
  const { toast, toastError } = useToast()

  const loadModule = useCallback(async () => {
    if (!id) return
    try {
      const [mod, types] = await Promise.all([
        api.get<ModuleData>(`/device-modules/${id}`),
        api.get<PortType[]>('/device-modules/port-types'),
      ])
      setModule(mod)
      setPortTypes(types)
      if (mod.design_data) {
        try { setDesign(JSON.parse(mod.design_data)) } catch { /* invalid JSON */ }
      }
    } catch (err) { toastError('Failed to load module', err) }
    finally { setLoading(false) }
  }, [id, toastError])

  useEffect(() => { loadModule() }, [loadModule])

  // Generate SVG preview on design changes
  const refreshPreview = useCallback(async () => {
    if (!module) return
    try {
      const result = await api.post<{ svg: string }>('/device-modules/generate-svg', {
        design_data: JSON.stringify(design),
        chassis: {
          width: module.chassis_width, height: module.chassis_height,
          color: module.chassis_color, material: module.chassis_material,
          cornerRadius: module.corner_radius, mountingEars: !!module.mounting_ears,
          name: module.name, modelNumber: module.model_number || '',
        },
      })
      setSvgPreview(result.svg)
    } catch { /* silent */ }
  }, [design, module])

  useEffect(() => { refreshPreview() }, [refreshPreview])

  // Auto-save
  useEffect(() => {
    if (!module) return
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current)
    autoSaveTimer.current = setTimeout(async () => {
      try {
        await api.patch(`/device-modules/${module.id}`, { design_data: JSON.stringify(design) })
      } catch { /* silent auto-save */ }
    }, 5000)
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current) }
  }, [design, module])

  async function handleSave() {
    if (!module) return
    setSaving(true)
    try {
      await api.patch(`/device-modules/${module.id}`, {
        name: module.name, manufacturer: module.manufacturer, model_number: module.model_number,
        mpn: module.mpn, form_factor: module.form_factor, chassis_color: module.chassis_color,
        chassis_material: module.chassis_material, chassis_width: module.chassis_width,
        chassis_height: module.chassis_height, corner_radius: module.corner_radius,
        mounting_ears: module.mounting_ears, design_data: JSON.stringify(design),
        is_template: module.is_template, tags: module.tags,
        catalog_product_id: module.catalog_product_id, device_spec_id: module.device_spec_id,
      })
      toast('Saved')
    } catch (err) { toastError('Failed to save', err) }
    finally { setSaving(false) }
  }

  async function handlePublish() {
    if (!module) return
    try {
      await handleSave()
      const updated = await api.post<ModuleData>(`/device-modules/${module.id}/publish`)
      setModule(updated)
      toast('Published')
    } catch (err) { toastError('Failed to publish', err) }
  }

  function updateChassis(field: string, value: unknown) {
    setModule(prev => prev ? { ...prev, [field]: value } : null)
  }

  function addPortGroup() {
    setDesign(prev => ({
      ...prev,
      portGroups: [...prev.portGroups, { type: 'rj45', count: 8, startX: 60, startY: 16, spacing: 20, label: 'auto' }],
    }))
    setSelectedGroup(design.portGroups.length)
  }

  function updatePortGroup(index: number, field: string, value: unknown) {
    setDesign(prev => {
      const groups = [...prev.portGroups]
      groups[index] = { ...groups[index], [field]: value }
      return { ...prev, portGroups: groups }
    })
  }

  function removePortGroup(index: number) {
    setDesign(prev => ({ ...prev, portGroups: prev.portGroups.filter((_, i) => i !== index) }))
    setSelectedGroup(null)
  }

  function addLED() {
    setDesign(prev => ({
      ...prev,
      leds: [...prev.leds, { type: 'system', x: 30, y: 15, size: 4 }],
    }))
  }

  function updateLED(index: number, field: string, value: unknown) {
    setDesign(prev => {
      const leds = [...prev.leds]
      leds[index] = { ...leds[index], [field]: value }
      return { ...prev, leds }
    })
  }

  function removeLED(index: number) {
    setDesign(prev => ({ ...prev, leds: prev.leds.filter((_, i) => i !== index) }))
  }

  // Keyboard shortcuts
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if (e.key === 's' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handleSave() }
      if (e.key === 'p' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); handlePublish() }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  })

  if (loading) return <CardGridSkeleton />
  if (!module) return <div style={{ padding: 40, color: 'var(--text-secondary)' }}>Module not found</div>

  const TABS = [
    { id: 'chassis' as const, label: 'Chassis' },
    { id: 'ports' as const, label: 'Ports' },
    { id: 'leds' as const, label: 'LEDs' },
    { id: 'labels' as const, label: 'Labels' },
    { id: 'linking' as const, label: 'Linking' },
  ]

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 56px)' }}>
      {/* Header */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
        <button onClick={() => navigate('/platform/device-modules')} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 18 }}>
          ←
        </button>
        <div style={{ flex: 1 }}>
          <input
            value={module.name}
            onChange={e => updateChassis('name', e.target.value)}
            style={{ background: 'transparent', border: 'none', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)', width: '100%', outline: 'none' }}
          />
        </div>
        <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Auto-saves every 5s</span>
        <Button variant="secondary" onClick={handleSave} loading={saving} size="sm">
          Save
        </Button>
        <Button onClick={handlePublish} size="sm">Publish</Button>
      </div>

      {/* Main layout */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Left toolbox */}
        <div style={{ width: 280, borderRight: '1px solid var(--border-subtle)', overflow: 'auto', flexShrink: 0 }}>
          {/* Tab buttons */}
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)' }}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: 1, padding: '10px 0', fontSize: 11, fontWeight: 600,
                  background: activeTab === tab.id ? 'var(--bg-elevated)' : 'transparent',
                  border: 'none', borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                  color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  cursor: 'pointer',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div style={{ padding: 16 }}>
            {activeTab === 'chassis' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <FieldGroup label="Material">
                  <select className="select" value={module.chassis_material} onChange={e => updateChassis('chassis_material', e.target.value)}>
                    {MATERIALS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </FieldGroup>
                <FieldGroup label="Color">
                  <input type="color" value={module.chassis_color} onChange={e => updateChassis('chassis_color', e.target.value)} style={{ width: '100%', height: 32 }} />
                </FieldGroup>
                <FieldGroup label="Width">
                  <input className="input" type="number" value={module.chassis_width} onChange={e => updateChassis('chassis_width', +e.target.value)} />
                </FieldGroup>
                <FieldGroup label="Height">
                  <input className="input" type="number" value={module.chassis_height} onChange={e => updateChassis('chassis_height', +e.target.value)} />
                </FieldGroup>
                <FieldGroup label="Corner Radius">
                  <input className="input" type="number" value={module.corner_radius} onChange={e => updateChassis('corner_radius', +e.target.value)} />
                </FieldGroup>
                <FieldGroup label="Mounting Ears">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input type="checkbox" checked={!!module.mounting_ears} onChange={e => updateChassis('mounting_ears', e.target.checked ? 1 : 0)} />
                    Enabled
                  </label>
                </FieldGroup>
                <FieldGroup label="Manufacturer">
                  <input className="input" value={module.manufacturer || ''} onChange={e => updateChassis('manufacturer', e.target.value)} placeholder="e.g. Ubiquiti" />
                </FieldGroup>
                <FieldGroup label="Model Number">
                  <input className="input" value={module.model_number || ''} onChange={e => updateChassis('model_number', e.target.value)} placeholder="e.g. USW-24-PoE" />
                </FieldGroup>
                <FieldGroup label="MPN">
                  <input className="input" value={module.mpn || ''} onChange={e => updateChassis('mpn', e.target.value)} placeholder="Manufacturer Part Number" />
                </FieldGroup>
              </div>
            )}

            {activeTab === 'ports' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Button onClick={addPortGroup} icon={PlusIcon} size="sm" className="w-full">Add Port Group</Button>
                {design.portGroups.map((pg, i) => (
                  <div key={i} style={{
                    padding: 12, borderRadius: 8,
                    background: selectedGroup === i ? 'var(--accent-muted)' : 'var(--bg-elevated)',
                    border: selectedGroup === i ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
                    cursor: 'pointer',
                  }} onClick={() => setSelectedGroup(i)}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>Group {i + 1}: {pg.type}</span>
                      <button onClick={e => { e.stopPropagation(); removePortGroup(i) }} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14 }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <FieldGroup label="Type" compact>
                        <select className="select" value={pg.type} onChange={e => updatePortGroup(i, 'type', e.target.value)} style={{ fontSize: 11 }}>
                          {portTypes.map(pt => <option key={pt.name} value={pt.name}>{pt.label}</option>)}
                        </select>
                      </FieldGroup>
                      <FieldGroup label="Count" compact>
                        <input className="input" type="number" value={pg.count} onChange={e => updatePortGroup(i, 'count', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="X" compact>
                        <input className="input" type="number" value={pg.startX} onChange={e => updatePortGroup(i, 'startX', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="Y" compact>
                        <input className="input" type="number" value={pg.startY} onChange={e => updatePortGroup(i, 'startY', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="Spacing" compact>
                        <input className="input" type="number" value={pg.spacing} onChange={e => updatePortGroup(i, 'spacing', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="PoE" compact>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
                          <input type="checkbox" checked={!!pg.poe} onChange={e => updatePortGroup(i, 'poe', e.target.checked)} /> Yes
                        </label>
                      </FieldGroup>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'leds' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Button onClick={addLED} icon={PlusIcon} size="sm" className="w-full">Add LED</Button>
                {design.leds.map((led, i) => (
                  <div key={i} style={{ padding: 12, borderRadius: 8, background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, fontWeight: 600 }}>LED {i + 1}</span>
                      <button onClick={() => removeLED(i)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 14 }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      <FieldGroup label="Type" compact>
                        <select className="select" value={led.type} onChange={e => updateLED(i, 'type', e.target.value)} style={{ fontSize: 11 }}>
                          {LED_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </FieldGroup>
                      <FieldGroup label="Size" compact>
                        <input className="input" type="number" value={led.size} onChange={e => updateLED(i, 'size', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="X" compact>
                        <input className="input" type="number" value={led.x} onChange={e => updateLED(i, 'x', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                      <FieldGroup label="Y" compact>
                        <input className="input" type="number" value={led.y} onChange={e => updateLED(i, 'y', +e.target.value)} style={{ fontSize: 11 }} />
                      </FieldGroup>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'labels' && (
              <div style={{ padding: 8, color: 'var(--text-secondary)', fontSize: 13 }}>
                Labels are auto-generated from port groups. Model number is shown in the bottom-right corner.
              </div>
            )}

            {activeTab === 'linking' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <FieldGroup label="Catalog Product">
                  <input
                    className="input"
                    value={catalogSearch}
                    onChange={e => {
                      setCatalogSearch(e.target.value)
                      if (e.target.value.length >= 2) {
                        api.get<{ data: { id: string; name: string; mpn: string }[] }>(`/catalog?search=${encodeURIComponent(e.target.value)}&limit=10`)
                          .then(r => setCatalogResults(r.data || []))
                          .catch(() => setCatalogResults([]))
                      } else {
                        setCatalogResults([])
                      }
                    }}
                    placeholder="Search catalog..."
                  />
                  {catalogResults.length > 0 && (
                    <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6, marginTop: 4 }}>
                      {catalogResults.map(p => (
                        <div key={p.id} onClick={() => {
                          updateChassis('catalog_product_id', p.id)
                          if (p.mpn) updateChassis('mpn', p.mpn)
                          setCatalogSearch(p.name)
                          setCatalogResults([])
                        }} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border-subtle)' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-overlay)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div style={{ fontWeight: 500 }}>{p.name}</div>
                          {p.mpn && <div style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>{p.mpn}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                  {module.catalog_product_id && <div style={{ fontSize: 10, color: 'var(--text-tertiary)', marginTop: 2 }}>ID: {module.catalog_product_id}</div>}
                </FieldGroup>
                <FieldGroup label="Device Spec">
                  <input
                    className="input"
                    value={module.device_spec_id || ''}
                    onChange={e => {
                      updateChassis('device_spec_id', e.target.value)
                      if (e.target.value.length >= 2) {
                        api.get<{ id: string; name: string }[]>(`/device-specs?search=${encodeURIComponent(e.target.value)}&limit=10`)
                          .then(r => setDeviceSpecResults(Array.isArray(r) ? r : []))
                          .catch(() => setDeviceSpecResults([]))
                      } else {
                        setDeviceSpecResults([])
                      }
                    }}
                    placeholder="Search device specs..."
                  />
                  {deviceSpecResults.length > 0 && (
                    <div style={{ maxHeight: 120, overflowY: 'auto', border: '1px solid var(--border-subtle)', borderRadius: 6, marginTop: 4 }}>
                      {deviceSpecResults.map(s => (
                        <div key={s.id} onClick={() => {
                          updateChassis('device_spec_id', s.id)
                          setDeviceSpecResults([])
                        }} style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border-subtle)' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-overlay)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          {s.name}
                        </div>
                      ))}
                    </div>
                  )}
                </FieldGroup>
                <FieldGroup label="MPN">
                  <input className="input" value={module.mpn || ''} onChange={e => updateChassis('mpn', e.target.value)} placeholder="Auto-filled or type MPN" />
                </FieldGroup>
                <FieldGroup label="Template">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                    <input type="checkbox" checked={!!module.is_template} onChange={e => updateChassis('is_template', e.target.checked ? 1 : 0)} />
                    Save as template
                  </label>
                </FieldGroup>
                <FieldGroup label="Tags">
                  <input className="input" value={module.tags || ''} onChange={e => updateChassis('tags', e.target.value)} placeholder="comma-separated tags" />
                </FieldGroup>
              </div>
            )}
          </div>
        </div>

        {/* Center canvas + preview toolbar */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--bg-0)' }}>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto' }}>
            <div
              style={{ padding: 40, cursor: dragging ? 'grabbing' : 'default' }}
              onMouseMove={e => {
                if (!dragging) return
                const dx = (e.clientX - dragging.startX) / 2 // account for scale(2)
                const dy = (e.clientY - dragging.startY) / 2
                if (dragging.type === 'port') {
                  updatePortGroup(dragging.index, 'startX', Math.round(dragging.origX + dx))
                  updatePortGroup(dragging.index, 'startY', Math.round(dragging.origY + dy))
                } else if (dragging.type === 'led') {
                  updateLED(dragging.index, 'x', Math.round(dragging.origX + dx))
                  updateLED(dragging.index, 'y', Math.round(dragging.origY + dy))
                }
              }}
              onMouseUp={() => setDragging(null)}
              onMouseLeave={() => setDragging(null)}
            >
              {svgPreview ? (
                <div
                  dangerouslySetInnerHTML={{ __html: applyPortPreview(svgPreview, portPreviewMode) }}
                  style={{ transform: 'scale(2)', transformOrigin: 'center' }}
                  onMouseDown={e => {
                    const target = e.target as SVGElement
                    const groupIndex = target.closest('[data-port-group]')?.getAttribute('data-port-group')
                    const ledIndex = target.closest('[data-led]')?.getAttribute('data-led')
                    if (groupIndex !== null && groupIndex !== undefined) {
                      const idx = parseInt(groupIndex)
                      const pg = design.portGroups[idx]
                      if (pg) {
                        setDragging({ type: 'port', index: idx, startX: e.clientX, startY: e.clientY, origX: pg.startX, origY: pg.startY })
                      }
                    } else if (ledIndex !== null && ledIndex !== undefined) {
                      const idx = parseInt(ledIndex)
                      const led = design.leds[idx]
                      if (led) {
                        setDragging({ type: 'led', index: idx, startX: e.clientX, startY: e.clientY, origX: led.x, origY: led.y })
                      }
                    }
                  }}
                />
              ) : (
                <div style={{ color: 'var(--text-tertiary)', fontSize: 14 }}>Generating preview...</div>
              )}
            </div>
          </div>

          {/* Port State Preview Toolbar */}
          <div style={{
            padding: '8px 16px', borderTop: '1px solid var(--border-subtle)',
            display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0,
          }}>
            <span style={{ fontSize: 11, color: 'var(--text-tertiary)', marginRight: 4 }}>Port Preview:</span>
            {([
              { mode: 'normal' as const, label: 'Normal' },
              { mode: 'all_off' as const, label: 'All Off' },
              { mode: 'all_active' as const, label: 'All Active' },
              { mode: 'mixed' as const, label: 'Mixed' },
              { mode: 'poe' as const, label: 'PoE' },
            ]).map(opt => (
              <button
                key={opt.mode}
                onClick={() => setPortPreviewMode(opt.mode)}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600,
                  border: portPreviewMode === opt.mode ? '1px solid var(--accent)' : '1px solid var(--border-subtle)',
                  background: portPreviewMode === opt.mode ? 'var(--accent-muted)' : 'transparent',
                  color: portPreviewMode === opt.mode ? 'var(--accent)' : 'var(--text-tertiary)',
                  cursor: 'pointer',
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function applyPortPreview(svg: string, mode: string): string {
  if (mode === 'normal') return svg
  // Apply CSS class-based coloring to port rectangles in the SVG
  const portColorMap: Record<string, string> = {
    all_off: '#333',
    all_active: '#22c55e',
    mixed: '#22c55e', // will be randomized below
    poe: '#fbbf24',
  }
  const color = portColorMap[mode] || '#333'
  // Replace port fill colors in the SVG
  return svg.replace(/class="port-rect"[^>]*fill="[^"]*"/g, (match) => {
    if (mode === 'mixed') {
      const randomColor = Math.random() > 0.5 ? '#22c55e' : '#333'
      return match.replace(/fill="[^"]*"/, `fill="${randomColor}"`)
    }
    return match.replace(/fill="[^"]*"/, `fill="${color}"`)
  })
}

function FieldGroup({ label, compact, children }: { label: string; compact?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: compact ? 10 : 11, color: 'var(--text-tertiary)', marginBottom: compact ? 2 : 4, fontWeight: 500 }}>
        {label}
      </label>
      {children}
    </div>
  )
}
